import { useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { CalendarDotsIcon } from '@phosphor-icons/react';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { Popover, PopoverContent } from '@voll-intelligence/ui-kit/ui/popover';
import { NARROW_STAGE_QUERY } from '../narrow_stage.js';
import { DateSheetBody } from '../date_sheet_body.js';
import { PageFieldSheet } from '../page_field_sheet.js';
import {
  TravelCalendar,
  type DateRange,
  type TravelCalendarBaseProps,
} from '../travel_calendar.js';
import { TravelCalendarConfirm } from '../travel_calendar_confirm.js';
import {
  FIELD_CONTROL_SIZE,
  FieldShell,
  fieldMessageId,
  fieldToneBorder,
  type FieldShellExtras,
} from './field_shell.js';

/**
 * The FORM date fields: a kit field (`FieldShell` + calendar icon) that opens the same calendar
 * as the hotel, car and bus searches — a popover of one month on the wide stage, the full-screen
 * sheet below `sm` (`NARROW_STAGE_QUERY`). The calendar edits a DRAFT born on every opening; only
 * Confirm commits it, and closing drops it. Dates are `YYYY-MM-DD`; every word comes from `copy`.
 */

export type DateFieldCopy = {
  /** The sheet title and the popover's accessible name ("Data da despesa"). */
  readonly title: string;
  /** The empty trigger ("Escolher"). */
  readonly placeholder: string;
  readonly confirm: string;
  /** The sheet's back arrow. */
  readonly back: string;
  readonly previousMonth: string;
  readonly nextMonth: string;
  /** A picked day on the trigger. */
  readonly format: (day: string) => string;
  /** The draft above Confirm; called with at least the first day. */
  readonly summary: (draft: DateRange) => string;
  /** What a `required` field adds to its description ("Obrigatório"): a button has no
   *  `aria-required`, so the native input's "required" is said in words. */
  readonly required?: string;
};

/** What the calendar of a date field needs, with or without the kit's trigger. */
type PickerBase = {
  readonly copy: DateFieldCopy;
  readonly locale: string;
  /** See `TravelCalendar.timeline`: `past` for a day already lived (an expense), `free` for a
   *  date with no side (a company's custom field). */
  readonly timeline?: TravelCalendarBaseProps['timeline'];
  readonly minDate?: string;
  readonly maxDate?: string;
  /** `dropdown`: month and year menus, one month at a time even in the sheet — a birthdate,
   *  decades away, where scrolling would be endless. */
  readonly caption?: 'label' | 'dropdown';
};

type DateFieldBase = FieldShellExtras &
  PickerBase & {
    /** Locks the field (a request in flight) without taking it out of the tab order. */
    readonly disabled?: boolean;
    /** The form will not go without a day: assistive tech hears `copy.required`. Blocking the
     *  submission stays with the form. */
    readonly required?: boolean;
  };

type Committed = { readonly from: string; readonly to: string };

/** The calendar's part of a field's props. */
function pickerBase({ copy, locale, timeline, minDate, maxDate, caption }: PickerBase): PickerBase {
  return {
    copy,
    locale,
    ...(timeline ? { timeline } : {}),
    ...(minDate ? { minDate } : {}),
    ...(maxDate ? { maxDate } : {}),
    ...(caption ? { caption } : {}),
  };
}

/** The words a required field adds to its description; none when it is optional. */
function requiredWords({ required, copy }: DateFieldBase): string | undefined {
  return required ? copy.required : undefined;
}

/** What the shell carries besides the label — only these reach `FieldShell`. */
function shellExtras({ adornment, message, messageTone }: FieldShellExtras): FieldShellExtras {
  return { ...(adornment ? { adornment } : {}), message, messageTone };
}

/** The calendar of one opening and its Confirm, which lights up once the choice is complete. */
function CalendarDraft({
  single,
  minNights,
  initial,
  sheet,
  base,
  onConfirm,
}: {
  readonly single: boolean;
  readonly minNights: 0 | 1;
  readonly initial: DateRange;
  readonly sheet: boolean;
  readonly base: PickerBase;
  readonly onConfirm: (range: Committed) => void;
}): ReactNode {
  const { copy, locale, timeline, minDate, maxDate, caption } = base;
  const [draft, setDraft] = useState<DateRange>(initial);
  // Without a minimum night the first touch already is a one-day range: it only lacks the end,
  // which the Confirm takes from the start. A stay (`minNights: 1`) still needs both ends.
  const complete =
    single || minNights === 0 ? Boolean(draft.from) : Boolean(draft.from && draft.to);
  const selection = single ? ({ mode: 'single' } as const) : ({ minNights } as const);
  // The menus page one month at a time; in the sheet that month sits where the column would.
  const paged = sheet && caption === 'dropdown';
  const footer = (
    <TravelCalendarConfirm
      summary={draft.from ? copy.summary(draft) : ''}
      confirmLabel={copy.confirm}
      disabled={!complete}
      full={sheet}
      onConfirm={() => {
        if (!draft.from) return;
        onConfirm({ from: draft.from, to: single ? draft.from : (draft.to ?? draft.from) });
      }}
    />
  );
  const calendar = (
    <TravelCalendar
      {...selection}
      layout={sheet && !paged ? 'sheet' : 'popover'}
      months={1}
      value={draft}
      locale={locale}
      previousMonthLabel={copy.previousMonth}
      nextMonthLabel={copy.nextMonth}
      {...(timeline ? { timeline } : {})}
      {...(minDate ? { minDate } : {})}
      {...(maxDate ? { maxDate } : {})}
      {...(caption ? { caption } : {})}
      onChange={setDraft}
      {...(paged ? {} : { footer })}
    />
  );
  if (!paged) return calendar;
  return (
    <DateSheetBody footer={footer}>
      <div className="px-2">{calendar}</div>
    </DateSheetBody>
  );
}

/** One trigger: the kit label and the picked day, or the placeholder. */
export function DateTrigger({
  label,
  text,
  open,
  disabled = false,
  required,
  triggerRef,
  extras,
  pill = false,
  onOpen,
}: {
  readonly label: string;
  readonly text: string | undefined;
  readonly open: boolean;
  readonly disabled?: boolean | undefined;
  /** The "required" words, when the field is required. */
  readonly required?: string | undefined;
  readonly triggerRef?: RefObject<HTMLButtonElement | null>;
  readonly extras: FieldShellExtras;
  /** The full-width pill with the day centred and no visible label (the KM leg sheet). */
  readonly pill?: boolean;
  readonly onOpen: () => void;
}): ReactNode {
  const id = useId();
  if (pill)
    return (
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        // The label names the pill and hides its content, so the day reaches assistive tech here.
        aria-describedby={`${id}-value`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (!disabled) onOpen();
        }}
        className="flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-border bg-background px-2.5 text-[14px] leading-5 font-medium text-foreground outline-none focus-visible:ring-3 focus-visible:ring-ring/50 aria-disabled:pointer-events-none aria-disabled:opacity-50"
      >
        <CalendarDotsIcon aria-hidden className="size-4 shrink-0" />
        <span id={`${id}-value`} className="truncate">
          {text}
        </span>
      </button>
    );
  // The `<label>` names the button and hides its content from the name, so the picked day (or the
  // placeholder) reaches assistive tech as the description, ahead of any field message.
  const valueId = `${id}-value`;
  const requiredId = `${id}-required`;
  const describedBy = [
    valueId,
    ...(required ? [requiredId] : []),
    ...(extras.message ? [fieldMessageId(id)] : []),
  ].join(' ');
  return (
    <div className="min-w-0 flex-1">
      <FieldShell label={label} fieldId={id} {...extras}>
        <button
          id={id}
          ref={triggerRef}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-describedby={describedBy}
          // `aria-disabled`, not `disabled`: focus returns here when the sheet closes and must not
          // fall to the body.
          aria-disabled={disabled || undefined}
          onClick={() => {
            if (!disabled) onOpen();
          }}
          className={`${FIELD_CONTROL_SIZE} flex min-w-0 items-center gap-2 rounded-lg border border-input bg-transparent px-2.5 text-left text-sm outline-none pointer-coarse:text-base focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-disabled:pointer-events-none aria-disabled:opacity-50 aria-expanded:border-ring aria-expanded:ring-3 aria-expanded:ring-ring/50${fieldToneBorder(extras)} ${text ? 'text-foreground' : 'text-muted-foreground'}`}
        >
          <CalendarDotsIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <span id={valueId} className="truncate">
            {text}
          </span>
        </button>
        {/* `hidden`: a description target is read even when hidden, and so it is never stray
            text in the page for a screen reader browsing past the field. */}
        {required && (
          <span id={requiredId} hidden>
            {required}
          </span>
        )}
      </FieldShell>
    </div>
  );
}

/** The sheet on the narrow stage, the popover under the first trigger on the wide one. */
export function Picker({
  open,
  title,
  back,
  triggerRef,
  onClose,
  children,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly back: string;
  readonly triggerRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
  readonly children: (sheet: boolean) => ReactNode;
}): ReactNode {
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  if (narrow) {
    return (
      <PageFieldSheet
        open={open}
        onClose={onClose}
        returnFocusTo={triggerRef}
        title={title}
        backLabel={back}
      >
        {children(true)}
      </PageFieldSheet>
    );
  }
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {/* `w-80`: the card's cell measures the popover (`cqw`); `w-auto` shrinks it to nothing. */}
      <PopoverContent
        anchor={triggerRef}
        align="start"
        aria-label={title}
        className="w-80 gap-2 rounded-xl p-2"
      >
        {open && children(false)}
      </PopoverContent>
    </Popover>
  );
}

export type DatePickerProps = PickerBase & {
  readonly open: boolean;
  readonly onClose: () => void;
  /** What the popover hangs from, and where focus returns on Confirm. */
  readonly triggerRef: RefObject<HTMLElement | null>;
} & (
    | {
        readonly mode: 'single';
        /** Empty = nothing picked. */
        readonly value: string;
        readonly onConfirm: (day: string) => void;
      }
    | {
        readonly mode: 'range';
        /** `1`: the end never falls on the start (a stay). Absent, `0`. */
        readonly minNights?: 0 | 1;
        readonly value: DateRange;
        readonly onConfirm: (range: Committed) => void;
      }
  );

/**
 * The calendar of a date field without the kit's trigger, for a screen that keeps its own (a
 * chip, a filter pill, a profile row): the same popover or sheet, draft and Confirm.
 */
export function DatePicker(props: DatePickerProps): ReactNode {
  const { open, onClose, triggerRef } = props;
  const single = props.mode === 'single';
  return (
    <Picker
      open={open}
      title={props.copy.title}
      back={props.copy.back}
      triggerRef={triggerRef}
      onClose={onClose}
    >
      {(sheet) => (
        <CalendarDraft
          single={single}
          minNights={props.mode === 'range' ? (props.minNights ?? 0) : 0}
          initial={single ? (props.value ? { from: props.value } : {}) : props.value}
          sheet={sheet}
          base={props}
          onConfirm={(range) => {
            onClose();
            triggerRef.current?.focus();
            if (props.mode === 'single') props.onConfirm(range.from);
            else props.onConfirm(range);
          }}
        />
      )}
    </Picker>
  );
}

/** A single day: the day of an expense, the new end of a top-up, a KM leg. */
export function DateField({
  label,
  value,
  pill,
  onChange,
  ...base
}: DateFieldBase & {
  readonly label: string;
  /** Empty = nothing picked. */
  readonly value: string;
  /** The trigger as a full-width pill, label only for assistive tech. */
  readonly pill?: boolean;
  readonly onChange: (day: string) => void;
}): ReactNode {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <DateTrigger
        label={label}
        text={value ? base.copy.format(value) : base.copy.placeholder}
        open={open}
        disabled={base.disabled}
        required={requiredWords(base)}
        triggerRef={triggerRef}
        extras={shellExtras(base)}
        {...(pill ? { pill } : {})}
        onOpen={() => {
          setOpen(true);
        }}
      />
      <DatePicker
        {...pickerBase(base)}
        mode="single"
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        triggerRef={triggerRef}
        value={value}
        onConfirm={onChange}
      />
    </>
  );
}

/** Start and end side by side; either one opens the same period calendar. */
export function DateRangeField({
  labels,
  value,
  onChange,
  ...base
}: DateFieldBase & {
  readonly labels: { readonly start: string; readonly end: string };
  readonly value: DateRange;
  readonly onChange: (range: Committed) => void;
}): ReactNode {
  const startRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const show = () => {
    setOpen(true);
  };
  const text = (day: string | undefined) => (day ? base.copy.format(day) : base.copy.placeholder);
  return (
    <div className="flex gap-4">
      <DateTrigger
        label={labels.start}
        text={text(value.from)}
        open={open}
        disabled={base.disabled}
        required={requiredWords(base)}
        triggerRef={startRef}
        extras={shellExtras(base)}
        onOpen={show}
      />
      <DateTrigger
        label={labels.end}
        text={text(value.to)}
        open={open}
        disabled={base.disabled}
        required={requiredWords(base)}
        extras={{}}
        onOpen={show}
      />
      <DatePicker
        {...pickerBase(base)}
        mode="range"
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        triggerRef={startRef}
        value={value}
        onConfirm={onChange}
      />
    </div>
  );
}
